import axios from "axios";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const ArrowIcon = () => (
  <svg viewBox="0 0 16 16" width="13" height="13" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);
 
const TagIcon = () => (
  <svg viewBox="0 0 14 14" width="11" height="11" fill="currentColor">
    <path d="M6.5 1a1 1 0 01.707.293l5.5 5.5a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-5.5-5.5A1 1 0 012 6V2a1 1 0 011-1h3.5zM4.5 4a.5.5 0 100-1 .5.5 0 000 1z" />
  </svg>
);
 
export function ServiceCard({ service, index, isActive, onHover }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
 
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
 
  return (
    <div
      ref={ref}
      onMouseEnter={() => onHover(service._id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => {navigate(`/serviceDetails/${service._id}`)
    window.scrollTo({
      top : 0,
      behavior: "smooth"
    });
    }}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible
          ? isActive ? "translateY(-6px)" : "translateY(0)"
          : "translateY(48px)",
        transition: `opacity 0.6s ease ${index * 0.1}s,
                     transform 0.6s ease ${index * 0.1}s`,
        fontFamily: "'Barlow', 'Montserrat', sans-serif",
      }}
      className="relative flex flex-col cursor-pointer group"
    >
      {/* ── Card shell ── */}
      <div
        className="relative flex flex-col h-full rounded-3xl overflow-hidden transition-all duration-300"
        style={{
          background: "#fff",
          border: isActive
            ? "1.5px solid #4ade80"
            : "1.5px solid #e8f0e0",
          boxShadow: isActive
            ? "0 20px 48px rgba(74,222,128,0.16), 0 4px 16px rgba(0,0,0,0.06)"
            : "0 2px 16px rgba(0,0,0,0.05)",
        }}
      >
 
        {/* ── Image block ── */}
        <div className="relative overflow-hidden" style={{ height: "200px", flexShrink: 0 }}>
          <img
            src={service.mainImage}
            alt={service.heading}
            className="w-full h-full object-cover transition-transform duration-700"
            style={{ transform: isActive ? "scale(1.06)" : "scale(1)" }}
          />
 
          {/* Gradient overlay */}
          <div
            className="absolute inset-0 transition-opacity duration-400"
            style={{
              background: "linear-gradient(to top, rgba(10,30,10,0.65) 0%, rgba(0,0,0,0) 55%)",
              opacity: isActive ? 1 : 0.5,
            }}
          />
 
          {/* Category pill — top left */}
          <div
            className="absolute top-3.5 left-3.5 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{
              background: "rgba(13,45,13,0.75)",
              color: "#4ade80",
              backdropFilter: "blur(6px)",
              border: "0.5px solid rgba(74,222,128,0.3)",
            }}
          >
            <TagIcon />
            {service.category?.name || "Service"}
          </div>
 
          {/* Active indicator — top right */}
          {service.isActive && (
            <div
              className="absolute top-3.5 right-3.5 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold"
              style={{ background: "rgba(74,222,128,0.18)", color: "#4ade80", border: "0.5px solid rgba(74,222,128,0.4)" }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#4ade80", animation: isActive ? "pulse 1.5s infinite" : "none" }}
              />
              Live
            </div>
          )}
 
          {/* Bottom overlay: title */}
          <div className="absolute bottom-0 left-0 right-0 px-4 pb-3">
            <h3
              className="text-white font-black leading-tight"
              style={{
                fontSize: "clamp(15px, 1.6vw, 18px)",
                letterSpacing: "-0.01em",
                textShadow: "0 1px 4px rgba(0,0,0,0.4)",
              }}
            >
              {service.heading}
            </h3>
          </div>
        </div>
 
        {/* ── Body ── */}
        <div className="flex flex-col flex-1 p-5">
 
          {/* Description */}
         <div
            className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
            dangerouslySetInnerHTML={{ __html: service.description }}
          />
 
          {/* Specs row (from bulletDescription first 2) */}
          {service.bulletDescription?.length > 0 && (
            <div
              className="flex flex-wrap gap-2 mb-4 pb-4"
              style={{ borderBottom: "0.5px solid #e8f0e0" }}
            >
              {service.bulletDescription.slice(0, 2).map((b, i) => {
                const dash = b.indexOf(" - ");
                const label = dash > 0 ? b.slice(0, dash).trim() : null;
                const val = dash > 0 ? b.slice(dash + 3).trim() : b;
                return label ? (
                  <div
                    key={i}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs"
                    style={{ background: "#f0f7ea", color: "#3B6D11", border: "0.5px solid #c3d9b0" }}
                  >
                    <span className="font-semibold">{label}:</span>
                    <span className="font-normal text-gray-600">{val}</span>
                  </div>
                ) : null;
              })}
            </div>
          )}
 
          {/* CTA row */}
          <div className="flex items-center justify-between">
            <button
              className="flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-xl transition-all duration-200"
              style={{
                background: isActive
                  ? "linear-gradient(135deg, #6ee85a 0%, #3dcc40 100%)"
                  : "#f0f7ea",
                color: isActive ? "#0d2d0d" : "#3B6D11",
                border: "0.5px solid #c3d9b0",
                letterSpacing: "0.04em",
                boxShadow: isActive ? "0 4px 14px rgba(74,222,128,0.35)" : "none",
              }}
            >
              Learn More <ArrowIcon />
            </button>
 
          </div>
        </div>
 
        {/* ── Active left accent bar ── */}
        <div
          className="absolute left-0 top-0 bottom-0 w-1 rounded-l-3xl transition-all duration-300"
          style={{
            background: isActive
              ? "linear-gradient(to bottom, #6ee85a, #3dcc40)"
              : "transparent",
          }}
        />
      </div>
 
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

const SkeletonLoader = () => {
  return (
    <div
      className="relative flex flex-col h-full rounded-3xl overflow-hidden animate-pulse"
      style={{
        background: "#fff",
        border: "1.5px solid #e8f0e0",
        boxShadow:
          "0 20px 48px rgba(74,222,128,0.12), 0 4px 16px rgba(0,0,0,0.05)",
      }}
    >
      {/* Shimmer Overlay */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="shimmer" />
      </div>

      {/* ── Image block ── */}
      <div
        className="relative overflow-hidden bg-gray-200"
        style={{ height: "200px", flexShrink: 0 }}
      >
        {/* Category pill */}
        <div className="absolute top-3 left-3 w-16 h-6 rounded-full bg-gray-300" />

        {/* Status */}
        <div className="absolute top-3 right-3 w-14 h-6 rounded-full bg-gray-300" />

        {/* Title */}
        <div className="absolute bottom-3 left-4 right-4 space-y-2">
          <div className="h-4 bg-gray-300 rounded w-3/4" />
          <div className="h-4 bg-gray-300 rounded w-1/2" />
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-col flex-1 p-5 space-y-4">
        {/* Description */}
        <div className="space-y-2">
          <div className="h-3 bg-gray-300 rounded w-full" />
          <div className="h-3 bg-gray-300 rounded w-5/6" />
          <div className="h-3 bg-gray-300 rounded w-2/3" />
        </div>

        {/* Tags */}
        <div className="flex gap-2">
          <div className="h-6 w-16 bg-gray-300 rounded-full" />
          <div className="h-6 w-12 bg-gray-300 rounded-full" />
        </div>

        {/* Button */}
        <div className="flex justify-between items-center mt-auto">
          <div className="h-8 w-24 bg-gray-300 rounded-xl" />
          <div className="h-6 w-6 bg-gray-300 rounded-full" />
        </div>
      </div>

      {/* Accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-300 rounded-l-3xl" />
    </div>
  );
};

export default function ConstructionServices() {
  const [activeCard, setActiveCard] = useState(null);
  const [titleVisible, setTitleVisible] = useState(false);
  const titleRef = useRef(null);
  const [service, setService] = useState([]);
  const [loading, setLoding]  = useState(false);


  const fetchService = async()=>{
    try {
      setLoding(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/product/`);
      // console.log("data", res.data.products);
      setService(res.data.products);
    } catch (error) {
      console.log("Error", error);
    }finally{
      setLoding(false);
    }
  }

  useEffect(() => {
    // Load fonts
    fetchService();
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;700;800;900&family=DM+Sans:wght@400;500&display=swap";
    document.head.appendChild(link);

    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);


  return (
    <div
      className="flex items-center justify-center py-20 px-6 md:px-10 lg:px-12 xl:px-20"
      style={{
        background: "linear-gradient(135deg, #f0f4f8 0%, #e8edf5 50%, #f0f4f8 100%)",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #cbd5e1 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          opacity: 0.3,
        }}
      />

      <div className="relative w-full ">
        {/* Section label */}
        <div
          ref={titleRef}
          className="text-center mb-12"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-10 bg-green-400" />
            <span
              className="text-green-500 font-bold text-sm font-rajdhani tracking-[0.15em] uppercase"
            >
             [What We Do]
            </span>
            <div className="h-px w-10 bg-green-400" />
          </div>

          <h2
            className="font-bold uppercase text-gray-900 font-rajdhani text-2xl md:text-4xl lg:text-[45px] leading-8 tracking-wide"
          >
            Delivering Construction{" "}
            <span
              className="relative inline-block"
              style={{
                WebkitTextStroke: "2px #21d609",
                color: "transparent",
              }}
            >
              Trusted
            </span>{" "}
            Expertise
          </h2>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <SkeletonLoader key={i} />
            ))
          ) :
          service.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
              isActive={activeCard === service.id}
              onHover={setActiveCard}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        {/* <div
          className="text-center mt-12"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.7s ease 0.6s, transform 0.7s ease 0.6s",
          }}
        >
          <button
            className="group relative overflow-hidden bg-green-500 text-white font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-green-500/30"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.15em" }}
          >
            <span className="relative z-10">View All Services</span>
            <div
              className="absolute inset-0 bg-gray-900 transition-transform duration-500 origin-left scale-x-0 group-hover:scale-x-100"
              style={{ zIndex: 0 }}
            />
          </button>
        </div> */}
      </div>
    </div>
  );
}